import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Ticket, User, Check, CreditCard, Smartphone, Landmark, Loader2, Info, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { AirlineLogo, SliceRow } from "../components/FlightCard";
import { SeatSection, MealSection, BaggageSection, PolicySection, addonTotals } from "../components/AddOns";
import { LiveBook } from "../components/LiveBook";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";
import { http, money, dateLabel, errMsg, login, instantOff } from "../lib/api";

const Section = ({ icon: Icon, title, children, right }) => (
  <section className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Icon className="h-5 w-5 text-[#EC5B24]" /> {title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24] transition-colors";

const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false);
  document.body.appendChild(s);
});

const RoundTripLegs = ({ offer, legs, base, cur }) => {
  const ok = legs?.separate_total != null;
  const diff = ok ? base - legs.separate_total : 0;
  return (
    <div className="space-y-1.5 pb-2 border-b border-dashed" data-testid="fare-legs">
      {offer.slices.map((s, i) => (
        <div key={i} className="flex justify-between gap-2" data-testid={`fare-leg-${i}`}>
          <dt className="text-slate-500">{i ? "Return" : "Departure"} · {s.flight_no} {s.origin}→{s.destination}{legs?.derived === i ? " (rest of round-trip fare)" : ""}</dt>
          <dd className="font-mono whitespace-nowrap">{!legs ? "…" : ok ? money(legs.legs[i], cur) : "incl."}</dd>
        </div>
      ))}
      {ok && legs.derived == null && diff < 0 && <div className="rounded-lg bg-[#E8F5E9] text-[#0F9D58] text-xs font-bold px-3 py-2" data-testid="rt-saving-badge">You save {money(-diff, cur)} with this round trip vs 2 separate tickets</div>}
      {ok && diff !== 0 && !(legs.derived == null && diff < 0) && <div className="flex justify-between text-[#0F9D58]" data-testid="fare-leg-adjust"><dt>{diff < 0 ? "Round-trip saving" : "Round-trip fare adjustment"}</dt><dd className="font-mono">{diff < 0 ? "−" : "+"}{money(Math.abs(diff), cur)}</dd></div>}
    </div>
  );
};

const InsuranceCard = ({ on, setOn, price, pax, intl, money }) => (
  <section className={`rounded-2xl p-5 sm:p-6 border-2 transition-colors ${on ? "border-[#1C1A4A] bg-indigo-50" : "border-slate-200 bg-white"}`} data-testid="insurance-card">
    <div className="flex items-start gap-4">
      <HeartPulse className="h-8 w-8 text-[#EC5B24] shrink-0" />
      <div className="flex-1">
        <div className="font-heading font-bold text-lg">Medical emergency travel insurance — {money(price)} per traveller</div>
        <div className="text-sm text-slate-600">Covers hospital treatment and medical emergencies during your trip{intl ? ", including emergency medical evacuation abroad" : ""}. {pax > 1 ? `${money(price * pax)} for ${pax} travellers. ` : ""}Your policy document is emailed with your ticket.</div>
      </div>
      <Switch data-testid="insurance-toggle" checked={on} onCheckedChange={setOn} />
    </div>
  </section>
);

const PASSPORT_KEYS = [["passport_no", "Passport number"], ["nationality", "Nationality"], ["passport_country", "Issuing country"], ["dob", "Date of birth", "date"], ["passport_issue", "Passport issue date", "date"], ["passport_expiry", "Passport expiry", "date"]];

const PassportFields = ({ t, i, setT }) => (
  <div className="sm:col-span-3 rounded-xl bg-slate-50 border border-dashed p-3" data-testid={`traveller-${i}-passport`}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Passport details · required for international travel</div>
    <p className="text-[11px] text-slate-500 -mt-1 mb-2">Many countries need the passport to be valid for 6 months after your trip.</p>
    <div className="grid gap-2 sm:grid-cols-3">
      {PASSPORT_KEYS.map(([k, l, type]) => (
        <label key={k} className="text-[11px] font-semibold text-slate-500">{l}
          <input data-testid={`traveller-${i}-${k.replace(/_/g, "-")}`} type={type || "text"} max={["dob", "passport_issue"].includes(k) ? new Date().toISOString().slice(0, 10) : undefined} min={k === "passport_expiry" ? new Date().toISOString().slice(0, 10) : undefined} value={t[k] || ""} maxLength={k === "passport_no" ? 15 : 40}
            onChange={(e) => setT(i, k, k === "passport_no" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") : e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm text-slate-900 font-normal outline-none focus:border-[#EC5B24] bg-white" />
        </label>
      ))}
    </div>
  </div>
);

export default function Book() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, config } = useAuth();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState("");
  const [addonData, setAddonData] = useState(null);
  const [addons, setAddons] = useState({ seats: {}, meals: {}, baggage: [] });
  const [travellers, setTravellers] = useState([]);
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [assured, setAssured] = useState(false);
  const [insured, setInsured] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [paying, setPaying] = useState(false);
  const [demo, setDemo] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    http.get(`/flights/offer/${offerId}`).then(({ data }) => {
      setOffer(data);
      const s = data.search || { adults: 1, children: 0, infants: 0 };
      setTravellers([
        ...Array(s.adults).fill("adult"), ...Array(s.children).fill("child"), ...Array(s.infants).fill("infant"),
      ].map((type) => ({ type, title: type === "adult" ? "Mr" : "Mstr", first_name: "", last_name: "", nationality: "Indian", passport_country: "India" })));
    }).catch((e) => setError(errMsg(e)));
    http.get("/deals").then((r) => setCoupons(r.data));
    http.get(`/flights/offer/${offerId}/addons`).then((r) => setAddonData(r.data)).catch(() => {});
  }, [offerId]);

  useEffect(() => { if (user) setContact((c) => ({ ...c, email: c.email || user.email, phone: c.phone || user.phone || "" })); }, [user]);

  const extras = useMemo(() => addonTotals(addonData, addons), [addonData, addons]);
  const pax = travellers.length;
  const seatPax = travellers.filter((t) => t.type !== "infant").length;
  const isLive = offer?.source === "serpapi";
  const isRound = (offer?.slices?.length || 0) > 1;
  const [legs, setLegs] = useState(null);
  useEffect(() => {
    if (isLive && isRound && !offer.needs_return) http.get(`/flights/offer/${offerId}/leg-prices`).then((r) => setLegs(r.data)).catch(() => setLegs({ legs: [], separate_total: null }));
  }, [isLive, isRound, offer, offerId]);
  const base = offer?.total_amount || 0;
  const taxes = isLive ? 0 : Math.round(base * 0.12);
  const seatTotal = extras.seats;
  const assuredTotal = assured ? (config.assured_price ?? 900) * pax : 0;
  const serviceFee = (config.service_fee || 0) * pax;
  const insurancePrice = offer?.international ? config.insurance_intl ?? 799 : config.insurance_domestic ?? 299;
  const insuranceTotal = insured ? insurancePrice * pax : 0;
  const subtotal = base + seatTotal + extras.meals + extras.baggage + assuredTotal + insuranceTotal + serviceFee;
  const couponOff = isLive && !applied?.uncapped ? Math.min(applied?.discount || 0, serviceFee) : applied?.discount || 0;
  const instant = instantOff(base, config);
  const discount = Math.max(couponOff, instant);
  const usingCoupon = applied && couponOff > instant;
  const total = subtotal - discount;
  const cur = offer?.currency || "INR";

  useEffect(() => {
    if (!applied) return;
    http.post("/coupons/validate", { code: applied.code, amount: subtotal, offer_id: offerId })
      .then((r) => setApplied(r.data)).catch(() => { setApplied(null); toast.error("Coupon removed — cart no longer eligible"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const apply = async (c) => {
    try {
      const { data } = await http.post("/coupons/validate", { code: c, amount: subtotal, offer_id: offerId });
      setApplied(data); setCode(data.code); const off = isLive && !data.uncapped ? Math.min(data.discount, serviceFee) : data.discount;
      if (off > instant) toast.success(`${data.code} applied — you save ${money(off, cur)}`);
      else toast.info(`Your ₹${instant.toLocaleString("en-IN")} instant discount is bigger than ${data.code} (${money(off, cur)}), so we kept the instant discount`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setT = (i, k, v) => setTravellers(travellers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));

  const confirm = async (bookingId, payload = {}) => {
    const { data } = await http.post(`/bookings/${bookingId}/verify`, payload);
    toast.success("Booking confirmed!");
    navigate(`/booking/${data.booking_id}`);
  };

  const pay = async () => {
    if (travellers.some((t) => !t.first_name.trim() || !t.last_name.trim())) return toast.error("Please fill all traveller names");
    if (offer.international) {
      const bad = travellers.findIndex((t) => PASSPORT_KEYS.some(([k]) => !(t[k] || "").trim()) || !/^[A-Za-z0-9]{6,15}$/.test(t.passport_no || ""));
      if (bad >= 0) return toast.error(`Please fill valid passport details for traveller ${bad + 1}`);
    }
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) return toast.error("Enter a valid email");
    if (!/^\d{10}$/.test(contact.phone)) return toast.error("Enter a 10-digit mobile number");
    setPaying(true);
    try {
      const { data } = await http.post("/bookings", {
        offer_id: offerId, travellers, email: contact.email, phone: contact.phone, addons,
        coupon: applied?.code || null, assured, insurance: insured, notes: notes || null,
      });
      if (data.payment_mode === "upi") {
        navigate(`/pay/${data.booking_id}`);
      } else if (data.payment_mode === "razorpay") {
        if (!(await loadRazorpay())) throw new Error("Could not load Razorpay");
        new window.Razorpay({
          key: data.razorpay_key_id, amount: Math.round(data.amount * 100), currency: data.currency, order_id: data.order_id,
          name: "WanderNest", description: `Booking ${data.booking_id}`,
          prefill: { name: user.name, email: contact.email, contact: contact.phone }, theme: { color: "#EC5B24" },
          handler: (r) => confirm(data.booking_id, r).catch((e) => toast.error(errMsg(e))),
          modal: { ondismiss: () => setPaying(false) },
        }).open();
      } else {
        setDemo(data);
        setPaying(false);
      }
    } catch (e) {
      toast.error(errMsg(e, e.message)); setPaying(false);
    }
  };

  if (error) return <div className="min-h-screen bg-[#F4F6F9]"><Header /><div className="max-w-xl mx-auto mt-20 bg-white rounded-2xl p-10 text-center" data-testid="offer-error">{error}<button onClick={() => navigate("/")} className="block mx-auto mt-4 text-[#EC5B24] font-semibold">Search again</button></div></div>;
  if (!offer) return <div className="min-h-screen bg-[#F4F6F9]"><Header /><div className="max-w-5xl mx-auto mt-10 px-4 space-y-4"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div></div>;
  if (offer.needs_return) return <div className="min-h-screen bg-[#F4F6F9] pb-16"><Header /><LiveBook key={offer.id} offer={offer} /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F9] pb-16">
      <Header />
      <div className="mx-auto max-w-7xl px-4 py-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5 min-w-0">
          <Section icon={Ticket} title="Review your flight">
            {offer.slices.map((s, i) => (
              <div key={i} className={`flex flex-col sm:flex-row sm:items-center gap-4 ${i ? "mt-4 pt-4 border-t border-dashed" : ""}`}>
                <div className="flex items-center gap-3 sm:w-48">
                  <AirlineLogo code={s.airline_code || offer.airline_code} logo={s.airline_logo} />
                  <div><div className="text-sm font-semibold">{s.airline_name || offer.airline_name}</div><div className="text-xs text-slate-500">{s.flight_no} · {dateLabel(s.depart_at)}</div></div>
                </div>
                <SliceRow s={s} />
              </div>
            ))}
          </Section>

          <Section icon={User} title="Traveller details">
            <div className="space-y-4">
              {travellers.map((t, i) => (
                <div key={i} className="grid gap-3 sm:grid-cols-[110px_1fr_1fr] items-end">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1 capitalize">{t.type} {i + 1}</div>
                    <select data-testid={`traveller-${i}-title`} value={t.title} onChange={(e) => setT(i, "title", e.target.value)} className={inputCls}>
                      {(t.type === "adult" ? ["Mr", "Ms", "Mrs"] : ["Mstr", "Miss"]).map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </div>
                  <input data-testid={`traveller-${i}-first-name`} placeholder="First & middle name" value={t.first_name} onChange={(e) => setT(i, "first_name", e.target.value)} className={inputCls} />
                  <input data-testid={`traveller-${i}-last-name`} placeholder="Last name" value={t.last_name} onChange={(e) => setT(i, "last_name", e.target.value)} className={inputCls} />
                  {offer.international && <PassportFields t={t} i={i} setT={setT} />}
                </div>
              ))}
              <div className="grid gap-3 sm:grid-cols-2 pt-3 border-t">
                <input data-testid="contact-email" type="email" placeholder="Email (ticket will be sent here)" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} />
                <input data-testid="contact-phone" inputMode="numeric" maxLength={10} placeholder="10-digit mobile number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value.replace(/\D/g, "") })} className={inputCls} />
              </div>
            </div>
          </Section>

          {addonData && <>
            <SeatSection data={addonData} value={addons} onChange={setAddons} seatPax={seatPax} money={(n) => money(n, cur)} />
            <MealSection data={addonData} value={addons} onChange={setAddons} travellers={travellers} money={(n) => money(n, cur)} />
            <BaggageSection data={addonData} value={addons} onChange={setAddons} travellers={travellers} money={(n) => money(n, cur)} />
          </>}
          <Section icon={User} title="Special requests">
            <input data-testid="booking-notes-input" maxLength={500} placeholder="Wheelchair, travelling with pets, any other request..." value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </Section>
          {(
          <section className={`rounded-2xl p-5 sm:p-6 border-2 transition-colors ${assured ? "border-[#0F9D58] bg-[#E8F5E9]" : "border-slate-200 bg-white"}`} data-testid="assured-card">
            <div className="flex items-start gap-4">
              <ShieldCheck className="h-8 w-8 text-[#0F9D58] shrink-0" />
              <div className="flex-1">
                <div className="font-heading font-bold text-lg">Full refund protection — {money(config.assured_price ?? 900, cur)} per traveller</div>
                <div className="text-sm text-slate-600">Cancel your trip anytime up to 3 hours before departure and get a <b>full refund</b> of your fare, seats, meals and baggage. No cancellation charge, no airline fees deducted. {pax > 1 ? `${money((config.assured_price ?? 900) * pax, cur)} for ${pax} travellers. ` : ""}The protection fee itself is non-refundable.</div>
              </div>
              <Switch data-testid="assured-toggle" checked={assured} onCheckedChange={setAssured} />
            </div>
          </section>
          )}
          <InsuranceCard on={insured} setOn={setInsured} price={insurancePrice} pax={pax} intl={offer.international} money={(n) => money(n, cur)} />
          <PolicySection cancelFee={config.cancel_fee ?? 900} assured={assured} pax={pax} money={(n) => money(n, cur)} />
        </div>

        <aside className="space-y-5 lg:sticky lg:top-20 h-fit">
          <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="coupon-panel">
            <h3 className="font-heading font-bold mb-3">Offers & coupons</h3>
            {isLive && <p className="text-[11px] text-slate-500 -mt-2 mb-3" data-testid="live-coupon-note">{instant > 0 ? `Your ₹${instant.toLocaleString("en-IN")} instant discount is already applied. A coupon is used only if it saves more (e.g. FLYTODAY).` : "On live fares, coupon savings are capped at the service fee (FLYTODAY gives its full discount)."}</p>}
            {applied && !usingCoupon && instant > 0 && <p className="text-[11px] font-semibold text-amber-600 mb-2" data-testid="coupon-not-used-note">Instant discount is bigger, so {applied.code} is not used.</p>}
            <div className="flex gap-2">
              <input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter coupon code" className={`${inputCls} font-mono`} />
              {applied ? (
                <button data-testid="coupon-remove-btn" onClick={() => { setApplied(null); setCode(""); }} className="text-sm font-bold text-red-500 px-3">Remove</button>
              ) : (
                <button data-testid="coupon-apply-btn" onClick={() => code && apply(code)} className="text-sm font-bold text-[#EC5B24] px-3">Apply</button>
              )}
            </div>
            <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
              {coupons.map((c) => (
                <button key={c.code} data-testid={`coupon-option-${c.code}`} onClick={() => apply(c.code)}
                  className={`w-full text-left rounded-xl border border-dashed p-3 transition-colors ${applied?.code === c.code ? "border-[#0F9D58] bg-[#E8F5E9]" : "border-slate-300 hover:border-[#EC5B24]"}`}>
                  <div className="flex items-center justify-between"><span className="font-mono text-sm font-bold">{c.code}</span>{applied?.code === c.code && <Check className="h-4 w-4 text-[#0F9D58]" />}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.title} · {c.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="fare-summary">
            <h3 className="font-heading font-bold mb-3">Fare summary</h3>
            <dl className="space-y-2 text-sm">
              {isRound && <RoundTripLegs offer={offer} legs={legs} base={base} cur={cur} />}
              {[...(isLive ? [[isRound ? "Round-trip airfare (both flights)" : "Airfare (incl. taxes)", base]] : [["Base fare", base - taxes], ["Taxes & fees", taxes]]),
                ["Full refund protection", assuredTotal], ["Medical travel insurance", insuranceTotal], ["Seats", seatTotal], ["Meals", extras.meals], ["Extra baggage", extras.baggage], ["Service fee", serviceFee]].filter(([l, v]) => v || l === "Service fee" || l.startsWith("Airfare") || l === "Base fare").map(([l, v]) => (
                <div key={l} className="flex justify-between"><dt className="text-slate-500">{l}{l === "Service fee" && pax > 1 ? ` (${pax} × ${money(config.service_fee, cur)})` : ""}</dt><dd className="font-mono" data-testid={l === "Service fee" ? "fare-service-fee" : undefined}>{money(v, cur)}</dd></div>
              ))}
              {discount > 0 && <div className="flex justify-between text-[#0F9D58] font-semibold"><dt>{usingCoupon ? `Coupon (${applied.code})` : "WanderNest instant discount"}</dt><dd className="font-mono" data-testid="fare-discount">−{money(discount, cur)}</dd></div>}
              <div className="flex justify-between pt-3 border-t text-base font-bold"><dt>Total</dt><dd className="font-mono text-xl text-[#EC5B24]" data-testid="fare-total">{money(total, cur)}</dd></div>
            </dl>
            {user ? (
              <button data-testid="pay-now-btn" onClick={pay} disabled={paying}
                className="mt-5 w-full rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] disabled:opacity-60 text-white font-heading font-bold text-lg py-3 flex items-center justify-center gap-2 transition-colors">
                {paying && <Loader2 className="h-5 w-5 animate-spin" />} Pay {money(total, cur)}
              </button>
            ) : (
              <button data-testid="login-to-book-btn" onClick={login} className="mt-5 w-full rounded-xl bg-[#1C1A4A] text-white font-bold py-3">Log in to book</button>
            )}
            {config.payment_mode === "upi" && (
              <p className="text-[11px] text-slate-500 mt-3 flex gap-1.5" data-testid="upi-payment-note"><Info className="h-3.5 w-3.5 shrink-0" /> Pay securely by UPI to WanderNest. We book your ticket once the payment is verified and share the PNR in My Trips.</p>
            )}
            {config.payment_mode === "demo" && (
              <p className="text-[11px] text-amber-700 mt-3 flex gap-1.5" data-testid="demo-payment-note"><Info className="h-3.5 w-3.5 shrink-0" /> Payments are in demo mode until Razorpay keys are connected. No money is charged.</p>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={!!demo} onOpenChange={(o) => !o && setDemo(null)}>
        <DialogContent data-testid="demo-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Complete payment (demo)</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-500">Booking {demo?.booking_id} · <span className="font-mono font-bold text-slate-900">{money(demo?.amount, demo?.currency)}</span></div>
          <div className="grid gap-2 mt-2">
            {[[Smartphone, "UPI"], [CreditCard, "Credit / Debit card"], [Landmark, "Net banking"]].map(([I, l], i) => (
              <label key={l} className="flex items-center gap-3 rounded-xl border p-3 cursor-pointer has-[:checked]:border-[#EC5B24] has-[:checked]:bg-orange-50">
                <input type="radio" name="pm" defaultChecked={!i} className="accent-[#EC5B24]" /> <I className="h-4 w-4" /> {l}
              </label>
            ))}
          </div>
          <button data-testid="demo-pay-confirm-btn" disabled={paying}
            onClick={async () => { setPaying(true); try { await confirm(demo.booking_id); } catch (e) { toast.error(errMsg(e)); setPaying(false); } }}
            className="w-full rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] text-white font-bold py-3 mt-2 flex items-center justify-center gap-2">
            {paying && <Loader2 className="h-4 w-4 animate-spin" />} Pay {money(demo?.amount, demo?.currency)}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
